import React, { useMemo } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { getFPS } from '../../utils/constants';
import { getFrameTolerance, snapToFrame } from '../../utils/timeUtils';
import { getFrameForTime } from '../../utils/frameIndex';

const OverlayContainer = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 180px;
  height: 180px;
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: ${theme.borderRadius.lg};
  padding: 0;
  border: none;
  box-shadow: ${theme.shadows.md};
  font-family: ${theme.typography.fontFamily};
  z-index: 100;
  pointer-events: none;
  overflow: hidden;
  
  /* Responsive sizing for smaller screens */
  @media screen and (max-width: 1440px) {
    width: 140px;
    height: 140px;
    top: 12px;
    left: 12px;
  }
`;


const OverlayCanvas = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: transparent;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
`;

const CourtLine = styled.line`
  stroke: #ffffff;
  stroke-width: 0.5;
  fill: none;
  opacity: 1;
`;

const PlayerDot = styled.div`
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${props => props.color};
  border: 1px solid ${props => props.color};
  transform: translate(-50%, -50%);
  z-index: 10;
  opacity: 1;
`;

// Court dimensions in meters (from the API)
const BASELINE = 10.9728;
const SIDELINE = 23.7744;
const SERVICE_LINE_DISTANCE = 1.3716;

// Reference court keypoints (from get_reference_court function)
const getReferenceCourtPoints = () => {
  const a = BASELINE / 2;
  const b = SIDELINE / 2;
  const c = SERVICE_LINE_DISTANCE;
  const d = a;
  
  return [
    [-a, -b, 0], // A
    [-a + c, -b, 0], // B
    [0, -b, 0], // C
    [a - c, -b, 0], // D
    [a, -b, 0], // E
    [c - a, d - b, 0], // F
    [0, d - b, 0], // G
    [a - c, d - b, 0], // H
    [c - a, b - d, 0], // I
    [0, b - d, 0], // J
    [a - c, b - d, 0], // K
    [-a, b, 0], // L
    [c - a, b, 0], // M
    [0, b, 0], // N
    [a - c, b, 0], // O
    [a, b, 0], // P
    [-a, 0, 0], // Q
    [a, 0, 0], // R
  ];
};


const TopViewPlotOverlay = ({ playerAnnotations = [], currentTime = 0 }) => {
  // Show only player annotations for the current frame that have meter coordinates
  const getCurrentFramePlayerAnnotations = () => {
    if (playerAnnotations.length === 0) return [];
    
    // Use frame index for exact frame matching
    const currentFrame = getFrameForTime(currentTime);
    
    return playerAnnotations.filter(ann => 
      getFrameForTime(ann.timestamp) === currentFrame &&
      ann.x_m !== undefined && ann.y_m !== undefined
    );
  };

  const currentFramePlayerAnnotations = getCurrentFramePlayerAnnotations();

  // Only show overlay if there are player annotations with meter coordinates for current frame
  if (currentFramePlayerAnnotations.length === 0) {
    return null;
  }

  const { courtLines, playerPositions } = useMemo(() => {
    const points = getReferenceCourtPoints();
    
    // Use a fixed square coordinate system (-15, 15) x (-15, 15)
    const bounds = {
      minX: -15,
      maxX: 15,
      minY: -15,
      maxY: 15
    };
    
    // Scale function to convert world coordinates to canvas coordinates (0-100)
    const scaleX = (x) => ((x - bounds.minX) / (bounds.maxX - bounds.minX)) * 100;
    const scaleY = (y) => ((bounds.maxY - y) / (bounds.maxY - bounds.minY)) * 100;
    
    // Define court line segments based on the correct tennis court layout
    const courtLines = [
      // Bottom baseline: A-B-C-D-E
      { x1: points[0][0], y1: points[0][1], x2: points[1][0], y2: points[1][1] }, // A-B
      { x1: points[1][0], y1: points[1][1], x2: points[2][0], y2: points[2][1] }, // B-C
      { x1: points[2][0], y1: points[2][1], x2: points[3][0], y2: points[3][1] }, // C-D
      { x1: points[3][0], y1: points[3][1], x2: points[4][0], y2: points[4][1] }, // D-E
      
      // Bottom service line: F-G-H
      { x1: points[5][0], y1: points[5][1], x2: points[6][0], y2: points[6][1] }, // F-G
      { x1: points[6][0], y1: points[6][1], x2: points[7][0], y2: points[7][1] }, // G-H
      
      // Top service line: I-J-K
      { x1: points[8][0], y1: points[8][1], x2: points[9][0], y2: points[9][1] }, // I-J
      { x1: points[9][0], y1: points[9][1], x2: points[10][0], y2: points[10][1] }, // J-K
      
      // Top baseline: L-M-N-O-P
      { x1: points[11][0], y1: points[11][1], x2: points[12][0], y2: points[12][1] }, // L-M
      { x1: points[12][0], y1: points[12][1], x2: points[13][0], y2: points[13][1] }, // M-N
      { x1: points[13][0], y1: points[13][1], x2: points[14][0], y2: points[14][1] }, // N-O
      { x1: points[14][0], y1: points[14][1], x2: points[15][0], y2: points[15][1] }, // O-P
      
      // Center line: Q-R
      { x1: points[16][0], y1: points[16][1], x2: points[17][0], y2: points[17][1] }, // Q-R
      
      // Left sideline: A-Q-L
      { x1: points[0][0], y1: points[0][1], x2: points[16][0], y2: points[16][1] }, // A-Q
      { x1: points[16][0], y1: points[16][1], x2: points[11][0], y2: points[11][1] }, // Q-L
      
      // Right sideline: E-R-P
      { x1: points[4][0], y1: points[4][1], x2: points[17][0], y2: points[17][1] }, // E-R
      { x1: points[17][0], y1: points[17][1], x2: points[15][0], y2: points[15][1] }, // R-P
      
      // Service line connections: B-F-J-M
      { x1: points[1][0], y1: points[1][1], x2: points[5][0], y2: points[5][1] }, // B-F
      { x1: points[5][0], y1: points[5][1], x2: points[8][0], y2: points[8][1] }, // F-I
      { x1: points[8][0], y1: points[8][1], x2: points[12][0], y2: points[12][1] }, // I-M
      
      // Service line connections: D-H-L-O  
      { x1: points[3][0], y1: points[3][1], x2: points[7][0], y2: points[7][1] }, // D-H
      { x1: points[7][0], y1: points[7][1], x2: points[10][0], y2: points[10][1] }, // H-K
      { x1: points[10][0], y1: points[10][1], x2: points[14][0], y2: points[14][1] }, // K-O
      
      // Center service line: G-K
      { x1: points[6][0], y1: points[6][1], x2: points[9][0], y2: points[9][1] }, // G-J
    ].map(line => ({
      ...line,
      x1Scaled: scaleX(line.x1),
      y1Scaled: scaleY(line.y1),
      x2Scaled: scaleX(line.x2),
      y2Scaled: scaleY(line.y2)
    }));
    
    // Process current frame player annotations with meter coordinates
    const playerPositions = currentFramePlayerAnnotations.map((annotation) => ({
      x: scaleX(annotation.x_m),
      y: scaleY(annotation.y_m),
      player: annotation.player,
      color: annotation.player === 'top' ? '#ff5555' : '#50fa7b'
    }));
    
    return { courtLines, playerPositions };
  }, [currentFramePlayerAnnotations]);
  
  return (
    <OverlayContainer>
      <OverlayCanvas>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
          style={{ position: 'absolute', top: 0, left: 0 }}
        >
          {/* Draw court lines */}
          {courtLines.map((line, index) => (
            <CourtLine
              key={index}
              x1={line.x1Scaled}
              y1={line.y1Scaled}
              x2={line.x2Scaled}
              y2={line.y2Scaled}
            />
          ))}
        </svg>
        
        {/* Draw player positions */}
        {playerPositions.map((position, index) => (
          <PlayerDot
            key={index}
            color={position.color}
            style={{
              left: `${position.x}%`,
              top: `${position.y}%`
            }}
          />
        ))}
      </OverlayCanvas>
    </OverlayContainer>
  );
};

export default TopViewPlotOverlay;
